import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPage = () => {
  const { t } = useLanguage();
  // Sample blog posts - in production, fetch from backend/CMS
  const blogPosts = [
    {
      id: 1,
      title: '10 Tips for a Successful Crowdfunding Campaign',
      excerpt: 'Learn the essential strategies that can help your campaign reach its goal faster.',
      author: 'Sarah Johnson',
      date: '2025-01-10',
      category: 'Tips & Guides',
      image: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=600',
    },
    {
      id: 2,
      title: 'How Medical Crowdfunding is Changing Lives',
      excerpt: 'Stories of hope and healing made possible by community support.',
      author: 'Michael Chen',
      date: '2025-01-08',
      category: 'Success Stories',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600',
    },
    {
      id: 3,
      title: 'The Power of Social Sharing for Your Campaign',
      excerpt: 'Maximize your reach with effective social media strategies.',
      author: 'Emily Davis',
      date: '2025-01-05',
      category: 'Marketing',
      image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=600',
    },
    {
      id: 4,
      title: 'Building Trust: The Importance of Campaign Transparency',
      excerpt: 'Why transparency matters and how to communicate effectively with your donors.',
      author: 'David Martinez',
      date: '2025-01-03',
      category: 'Best Practices',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600',
    },
    {
      id: 5,
      title: 'Emergency Fundraising: How to Launch Quickly and Effectively',
      excerpt: 'Step-by-step guide for urgent campaigns that need immediate support.',
      author: 'Rachel Williams',
      date: '2024-12-28',
      category: 'Tips & Guides',
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600',
    },
    {
      id: 6,
      title: 'From Zero to Hero: How One Family Raised $50,000 in 30 Days',
      excerpt: 'An inspiring story of community support and the power of perseverance.',
      author: 'James Thompson',
      date: '2024-12-25',
      category: 'Success Stories',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=600',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">{t('blogTitle')}</h1>
            <p className="text-xl text-muted-foreground">{t('blogSubtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link to={`/blog/${post.id}`} key={post.id}>
                <article className="bg-card border border-border rounded-xl overflow-hidden group hover:shadow-lg transition-shadow h-full">
                  <div className="aspect-video overflow-hidden bg-muted">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=600';
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-medium text-emerald-500 uppercase">{post.category}</span>
                    <h2 className="text-lg font-semibold text-foreground mt-2 mb-3 line-clamp-2 group-hover:text-emerald-500 transition-colors">{post.title}</h2>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
